import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { LocationUserTableRow } from '@/types/location-users'

interface RemoveLocationUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: LocationUserTableRow | null
  onConfirm: () => Promise<void>
  isLoading?: boolean
}

export function RemoveLocationUserDialog({
  open,
  onOpenChange,
  user,
  onConfirm,
  isLoading = false,
}: RemoveLocationUserDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm()
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove User from Location</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove {user.user_display_name || user.user_email} from this
            location?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col gap-1 p-3 rounded-lg bg-muted">
            <p className="font-medium text-sm">{user.user_display_name || 'Unnamed'}</p>
            <p className="text-xs text-muted-foreground">{user.user_email}</p>
          </div>

          <div className="text-sm text-muted-foreground">
            This user will no longer have access to this location. They can be reassigned later if needed.
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting ? 'Removing...' : 'Remove'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
